import { useContext } from "react";
import { useFormik } from "formik";
import DefaultLinkButton from "../../../atoms/buttons/DefaultLinkButton";
import DefaultButton from "../../../atoms/buttons/DefaultButton";
import { AuthContext } from "../../../../context/authContext";
import { useTranslation } from "react-i18next";
import { SignInValidation } from "./validations";
import PhoneInputField from "../../../molecules/formControls/inputField/PhoneInputField";
import FloatingInputField from "../../../molecules/formControls/inputField/FloatingInputField";
import TabsRoot from "../../../molecules/tabs/TabsRoot";
import ToggleSwitchWithLabel from "../../../molecules/formControls/switch/ToggleSwitchWithLabel";
import ExportLoginButton from "../../../atoms/buttons/ExportLoginButton";
import AppleIcon from "../../../atoms/images/AppleIcon";
import GoogleIcon from "../../../atoms/images/GoogleIcon";
import FacebookIcon from "../../../atoms/images/FacebookIcon";
import OutlineButton from "../../../atoms/buttons/OutlineButton";
import Text from "../../../molecules/content/Text";
import { useCognitoOAuth } from "../../../../hooks/useCognitoOAuth";
import { useNavigate } from "react-router-dom";
import { formattedPhone, getCountryCode } from "../../../../helpers/utils";
import { useToast } from "../../../../hooks/useToast";

const SignInForm = () => {
  const navigate = useNavigate();
  const authContext = useContext(AuthContext);
  const { t } = useTranslation();
  const { showToast } = useToast();
  let countryCode = getCountryCode();

  const initialValues = {
    loginMethod: "email",
    email: "",
    phone_number: "",
    countryCode: "+90",
    password: "",
    remember: false,
  };

  const onSubmit = async (values) => {
    try {
      await authContext.signInWithEmail(
        values.loginMethod === "email"
          ? values.email
          : formattedPhone(values.phone_number, countryCode),
        values.password
      );
    } catch {
      if (values.loginMethod === "phone_number") {
        showToast("error", t("signIn.phoneNumberErrorMessage"));
        return;
      }
      showToast("error", t("signIn.emailErrorMessage"));
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: SignInValidation(t, countryCode),
    onSubmit,
  });

  const { openProviderPopup } = useCognitoOAuth({
    remember: formik.values.remember,
    authenticateUser: authContext.authenticateUser,
  });

  const handleChangeTab = (value) => {
    formik.setFieldValue("loginMethod", value);

    if (value === "email") {
      formik.setTouched({
        ...formik.touched,
        phone_number: false,
        countryCode: false,
      });
    } else {
      formik.setTouched({
        ...formik.touched,
        email: false,
      });
    }
  };

  const handleChangeRemember = (value) => {
    formik.setFieldValue("remember", value);
  };

  const handleClickSmsLogin = () => {
    navigate("/sms-login");
  };

  const tabsData = [
    {
      value: "email",
      label: t("signIn.email"),
      content: (
        <FloatingInputField
          id="email"
          name="email"
          label={t("signIn.email")}
          required={true}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
          formikTouched={formik.touched.email}
          error={formik.errors.email}
        />
      ),
    },
    {
      value: "phone_number",
      label: t("signIn.phone"),
      content: (
        <PhoneInputField
          id="phone_number"
          name="phone_number"
          required={true}
          onChange={formik.handleChange}
          countryCode={countryCode}
          onBlur={formik.handleBlur}
          value={formik.values.phone_number}
          formikTouched={formik.touched.phone_number}
          error={formik.errors.phone_number}
        />
      ),
    },
  ];

  const handleClickLogout = (provider) => {
    sessionStorage.setItem("last_idp", provider);
    openProviderPopup(provider);
  };

  return (
    <form
      onSubmit={formik.handleSubmit}
      autoComplete="off"
      className="flex flex-col gap-8"
    >
      <div className="flex flex-col gap-4">
        <Text variant={"h2"} className="flex justify-center text-vestelColor">
          {t("signIn.welcome")}
        </Text>
        <TabsRoot
          tabs={tabsData}
          defaultValue="email"
          isSub
          onValueChange={handleChangeTab}
        />
        <FloatingInputField
          id="password"
          name="password"
          label={t("signIn.password")}
          required={true}
          type="password"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.password}
          formikTouched={formik.touched.password}
          error={formik.errors.password}
          showDefaultValidation={true}
        />
        <div className="flex flex-column justify-between">
          <ToggleSwitchWithLabel
            label={t("signIn.rememberMe")}
            checked={formik.values.remember}
            onChange={handleChangeRemember}
          />
          <DefaultLinkButton
            uri={"/request-code"}
            label={t("signIn.forgotPassword")}
            className="text-end"
          />
        </div>
      </div>
      <div className="flex flex-col gap-[12px]">
        <DefaultButton
          type="submit"
          label={t("signIn.login")}
          disabled={!(formik.isValid && formik.dirty) || formik.isSubmitting}
        />
        <OutlineButton
          label={t("signIn.smsLogin")}
          onClick={handleClickSmsLogin}
        />
        <Text className="flex justify-center w-full">{t("signIn.or")}</Text>
        <ExportLoginButton
          label={t("signIn.googleLogin")}
          icon={<GoogleIcon />}
          className="text-[#757575] border-[#EDEDED] bg-[#F3F3F3]"
          onClick={() => handleClickLogout("Google")}
        />
        <ExportLoginButton
          label={t("signIn.appleLogin")}
          icon={<AppleIcon size={25} />}
          className="text-white border-[#EDEDED] bg-[#000000]"
          onClick={() => handleClickLogout("SignInWithApple")}
        />
        <ExportLoginButton
          label={t("signIn.facebookLogin")}
          icon={<FacebookIcon size={15} />}
          className="text-white border-[#EDEDED] bg-[#1877F2]"
          onClick={() => handleClickLogout("Facebook")}
        />
      </div>
    </form>
  );
};

export default SignInForm;
